import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiCopy, FiUpload, FiHome, FiSmartphone, FiAlertTriangle } from 'react-icons/fi';
import styles from './DepositModal.module.css';
import axiosClient from '../../api/axiosClient';
import { showPaymentError } from '../../utils/kycErrorToast';

import { useCurrency } from '../../app/CurrencyContext';

type Currency = string;
type Method = 'bank' | 'upi';

interface BankAccount {
  currency: Currency;
  bank_name: string | null; account_holder: string | null;
  account_number: string | null; ifsc_code: string | null; upi_id: string | null;
}

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  initialPartnerTier?: string;
}

interface ApprovedTier {
  tier: string;
  investCapital: number;
}

const copy = (text: string) => {
  navigator.clipboard?.writeText(text);
  toast.success('Copied to clipboard');
};

const PORTAL_LIMITS: Record<string, { min: number; max: number }> = {
  INR: { min: 25000, max: 2500000 },
  USD: { min: 250, max: 25000 },
};

const PORTAL_OPTIONS = [
  { key: 'A', label: 'Portal A — Monthly capital + profit (25 months)' },
  { key: 'B', label: 'Portal B — Monthly capital + profit (15 months)' },
  { key: 'C', label: 'Portal C — 5% monthly profit, capital back after 25 months' },
];

const DepositModal: React.FC<Props> = ({ onClose, onSuccess, initialPartnerTier }) => {
  const { currencies: adminCurrencies, inrPerUnit } = useCurrency();
  const [currency, setCurrency] = useState<Currency>('INR');
  const [amount, setAmount] = useState<string>('');
  const [method, setMethod] = useState<Method>('bank');
  const [transactionId, setTransactionId] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMethodType, setPaymentMethodType] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [approvedTiers, setApprovedTiers] = useState<ApprovedTier[]>([]);
  const [selectedPartnerTier, setSelectedPartnerTier] = useState<string>(initialPartnerTier || '');
  const [selectedPortal, setSelectedPortal] = useState<string>('');
  const [ownedPortals, setOwnedPortals] = useState<string[]>([]);

  useEffect(() => {
    Promise.all([
      axiosClient.get('/portal/mine'),
      axiosClient.get('/funds/mine'),
    ]).then(([portalRes, fundsRes]) => {
      const owned: string[] = (portalRes.data.investments || []).map((inv: any) => inv.portal);
      const pendingPortals: string[] = (fundsRes.data.requests || [])
        .filter((r: any) => r.type === 'deposit' && ['pending', 'processing'].includes(r.status))
        .map((r: any) => /^Portal ([ABC]) Investment$/.exec(r.package_name || '')?.[1])
        .filter(Boolean);
      setOwnedPortals(Array.from(new Set([...owned, ...pendingPortals])));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axiosClient.get('/settings').then(({ data }) => {
      setBanks(data.bankAccounts);
    }).finally(() => setLoadingSettings(false));

    Promise.all([
      axiosClient.get('/partners/mine'),
      axiosClient.get('/partners/tiers'),
    ]).then(([appsRes, tiersRes]) => {
      const approvedTierNames: string[] = appsRes.data.applications
        .filter((a: any) => a.status === 'approved')
        .map((a: any) => a.tier);
      const list: ApprovedTier[] = tiersRes.data.tiers
        .filter((t: any) => approvedTierNames.includes(t.key))
        .map((t: any) => ({ tier: t.key, investCapital: t.investCapital }));
      setApprovedTiers(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedPartnerTier) return;
    const t = approvedTiers.find((x) => x.tier === selectedPartnerTier);
    if (t) {
      setCurrency('USD');
      setAmount(String(t.investCapital));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerTier, approvedTiers]);

  const bankAccount = banks.find((b) => b.currency === currency);
  const hasBankDetails = bankAccount && (bankAccount.bank_name || bankAccount.account_number);
  const upiId = bankAccount?.upi_id;

  const amountNum = Number(amount) || 0;
  const inrEquivalent = useMemo(() => amountNum * (inrPerUnit(currency) || 1), [amountNum, currency, inrPerUnit]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setProof(f);
  };

  const handleSubmit = async () => {
    if (!hasBankDetails) {
      toast.error(`Bank details for ${currency} haven't been configured yet. Please choose another currency or contact support.`); return;
    }
    if (!amountNum || amountNum <= 0) { toast.error('Enter a valid amount.'); return; }
    if (selectedPortal && ownedPortals.includes(selectedPortal)) {
      toast.error(`Portal ${selectedPortal} is unavailable — you already have an active investment or pending request there.`); return;
    }
    if (selectedPortal) {
      // Same ticket size as the server: INR 25,000 - 25 Lac, USD 250 - 25,000, any other
      // currency converted from the INR limits.
      const limits = PORTAL_LIMITS[currency] || {
        min: PORTAL_LIMITS.INR.min / (inrPerUnit(currency) || 1),
        max: PORTAL_LIMITS.INR.max / (inrPerUnit(currency) || 1),
      };
      if (amountNum < limits.min || amountNum > limits.max) {
        toast.error(`Portal investments must be between ${limits.min.toLocaleString('en-IN', { maximumFractionDigits: 2 })} and ${limits.max.toLocaleString('en-IN', { maximumFractionDigits: 2 })} ${currency}.`); return;
      }
    }
    if (!transactionId.trim()) { toast.error('Enter the Transaction ID / UTR.'); return; }
    if (!paymentDate) { toast.error('Select the payment date & time.'); return; }
    if (!paymentMethodType) { toast.error('Select a payment method type.'); return; }

    setSaving(true);
    try {
      const form = new FormData();
      form.append('type', 'deposit');
      form.append('amount', String(amountNum.toFixed(2)));
      form.append('amountInrEquivalent', String(inrEquivalent.toFixed(2)));
      form.append('transactionRef', transactionId);
      form.append('paymentDate', new Date(paymentDate).toISOString());
      form.append('paymentMethodType', paymentMethodType);
      form.append('currency', currency);
      if (selectedPartnerTier) form.append('packageName', `${selectedPartnerTier} Partner`);
      if (selectedPortal) form.append('packageName', `Portal ${selectedPortal} Investment`);
      form.append('exchangeRate', String(inrPerUnit(currency) || 1));
      if (proof) form.append('proof', proof);

      await axiosClient.post('/funds', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Deposit request submitted for review.');
      window.dispatchEvent(new Event('wallet:refresh'));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      showPaymentError(err, 'Could not submit payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2>Deposit Funds</h2>
            <p>Complete your payment securely</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        <div className={styles.body}>
          <div className={styles.col}>
            {approvedTiers.length > 0 && (
              <>
                <label className={styles.label}>Partner Program Payment (optional)</label>
                <select
                  className={styles.textInput}
                  style={{ marginBottom: 14 }}
                  value={selectedPartnerTier}
                  onChange={(e) => setSelectedPartnerTier(e.target.value)}
                >
                  <option value="">Not a partner payment — regular deposit</option>
                  {approvedTiers.map((t) => (
                    <option key={t.tier} value={t.tier}>{t.tier} Partner — ${t.investCapital.toLocaleString()}</option>
                  ))}
                </select>
              </>
            )}

            <label className={styles.label}>Portal Investment (optional)</label>
            <select
              className={styles.textInput}
              style={{ marginBottom: 6 }}
              value={selectedPortal}
              onChange={(e) => setSelectedPortal(e.target.value)}
            >
              <option value="">Not a Portal investment — regular deposit</option>
              {PORTAL_OPTIONS.map((p) => (
                <option key={p.key} value={p.key} disabled={ownedPortals.includes(p.key)}>
                  {p.label}{ownedPortals.includes(p.key) ? ' — unavailable' : ''}
                </option>
              ))}
            </select>
            {ownedPortals.length > 0 && (
              <div className={styles.dropzoneHint} style={{ marginBottom: 6 }}>
                You already have an active investment or a pending request in {ownedPortals.map((k) => `Portal ${k}`).join(', ')}.
                {ownedPortals.length < PORTAL_OPTIONS.length && ' You can still invest in the other Portals.'}
              </div>
            )}
            {selectedPortal && (
              <div className={styles.dropzoneHint} style={{ marginBottom: 14 }}>
                Ticket size: ₹25,000 – ₹25 Lac (or $250 – $25,000). Your team confirms and activates the payout schedule once this deposit is approved.
              </div>
            )}

            <label className={styles.label}>Select Currency</label>
            <div className={styles.currencyTabs}>
              {adminCurrencies.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  disabled={!!selectedPartnerTier && c.code !== 'USD'}
                  className={`${styles.currencyTab} ${currency === c.code ? styles.currencyTabActive : ''}`}
                  style={selectedPartnerTier && c.code !== 'USD' ? { opacity: .4, cursor: 'not-allowed' } : undefined}
                  onClick={() => setCurrency(c.code)}
                >
                  <span className={styles.currencyCode}>{c.symbol}</span>
                  <span className={styles.currencyName}>{c.code}</span>
                  <span className={styles.currencySub}>{c.name}</span>
                </button>
              ))}
            </div>
            {selectedPartnerTier && (
              <div className={styles.dropzoneHint} style={{ marginBottom: 6 }}>
                Partner tier pricing is fixed in USD, so currency is locked while a tier is selected.
              </div>
            )}

            <label className={styles.label}>Amount to Pay</label>
            <div className={styles.amountInput}>
              <span>{adminCurrencies.find((c) => c.code === currency)?.symbol || currency}</span>
              <input
                type="number"
                disabled={!!selectedPartnerTier}
                style={selectedPartnerTier ? { opacity: .7, cursor: 'not-allowed' } : undefined}
                min={0}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <label className={styles.label}>Payment Method</label>
            <div className={styles.methodToggle}>
              <button type="button" className={method === 'bank' ? styles.methodActive : ''} onClick={() => setMethod('bank')}>
                <FiHome /> Bank Transfer
              </button>
              <button type="button" className={method === 'upi' ? styles.methodActive : ''} onClick={() => setMethod('upi')}>
                <FiSmartphone /> UPI Payment
              </button>
            </div>

            <label className={styles.label}>Transaction ID / UTR</label>
            <input className={styles.textInput} value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="Enter transaction ID" />

            <label className={styles.label}>Payment Date &amp; Time</label>
            <input className={styles.textInput} type="datetime-local" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />

            <label className={styles.label}>Payment Method Type</label>
            <select className={styles.textInput} value={paymentMethodType} onChange={(e) => setPaymentMethodType(e.target.value)}>
              <option value="">Select payment method</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="UPI Payment">UPI Payment</option>
            </select>
          </div>

          <div className={styles.col}>
            {loadingSettings ? (
              <div className={styles.panel}>Loading payment details…</div>
            ) : method === 'bank' ? (
              <div className={styles.panel}>
                <div className={styles.panelTitle}><FiHome /> Bank Account Details</div>
                {hasBankDetails ? (
                  <>
                    {[
                      ['Bank Name', bankAccount?.bank_name],
                      ['Account Holder', bankAccount?.account_holder],
                      ['Account Number', bankAccount?.account_number],
                      ['IFSC Code', bankAccount?.ifsc_code],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div className={styles.detailRow} key={label}>
                        <span>{label}</span>
                        <div className={styles.detailValue}>
                          <b>{value}</b>
                          <FiCopy className={styles.copyIcon} onClick={() => copy(String(value))} />
                        </div>
                      </div>
                    ))}
                    <div className={styles.detailRow}>
                      <span>Amount to Transfer</span>
                      <b className={styles.amountHighlight}>₹{inrEquivalent.toFixed(2)} INR</b>
                    </div>
                  </>
                ) : (
                  <div className={styles.dropzoneHint} style={{ padding: '16px 0', textAlign: 'center' }}>
                    Bank details for {currency} haven't been configured yet. Please contact support or try another payment method.
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.panel}>
                {upiId ? (
                  <>
                    <div className={styles.qrWrap}>
                      <img
                        className={styles.qrImg}
                        alt="UPI QR code"
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=${encodeURIComponent(upiId)}%26am=${amountNum || 0}`}
                      />
                    </div>
                    <div className={styles.upiIdRow}>
                      <span>UPI ID</span>
                      <div className={styles.detailValue}>
                        <b>{upiId}</b>
                        <FiCopy className={styles.copyIcon} onClick={() => copy(upiId)} />
                      </div>
                    </div>
                    <div className={styles.upiAmountBox}>
                      <span>Amount to Pay</span>
                      <b>₹{inrEquivalent.toFixed(2)} INR</b>
                    </div>
                  </>
                ) : (
                  <div className={styles.dropzoneHint} style={{ padding: '16px 0', textAlign: 'center' }}>
                    UPI isn't configured for {currency} yet. Please use Bank Transfer instead.
                  </div>
                )}
              </div>
            )}

            <div className={styles.panel}>
              <div className={styles.panelTitle}><FiUpload /> Upload Payment Proof</div>
              <label className={styles.dropzone}>
                <FiUpload size={22} />
                <span>{proof ? proof.name : 'Click to upload screenshot'}</span>
                <span className={styles.dropzoneHint}>PNG, JPG up to 5MB</span>
                <input type="file" accept="image/png,image/jpeg" hidden onChange={handleFile} />
              </label>
            </div>

            <div className={styles.warningBox}>
              <FiAlertTriangle />
              <div>
                <b>Important</b>
                <p>Processing: 2–6 hours. Double-check all details before submitting.</p>
              </div>
            </div>

            <button className={styles.confirmBtn} onClick={handleSubmit} disabled={saving || !hasBankDetails}>
              {saving ? 'Submitting…' : !hasBankDetails ? 'Bank details not available' : 'Confirm Payment →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;